export const projects = {
  borsamex: {
    id: 'borsamex',
    name: 'Borsamex',
    category: {
      en: 'Financial Infrastructure',
      tr: 'Finansal Altyapı',
      de: 'Finanzinfrastruktur'
    },
    systemType: {
      en: 'AI-Powered Market Intelligence Platform',
      tr: 'Yapay Zeka Destekli Piyasa İstihbarat Platformu',
      de: 'KI-gestützte Marktintelligenz-Plattform'
    },
    tagline: {
      en: 'Real-time market analysis system with predictive AI',
      tr: 'Tahminsel yapay zeka ile gerçek zamanlı piyasa analiz sistemi',
      de: 'Echtzeit-Marktanalysesystem mit prädiktiver KI'
    },
    // ARCHITECTURE LAYERS - For Block Diagram
    architectureLayers: [
      {
        id: 'ingestion',
        name: { en: 'Data Ingestion Layer', tr: 'Veri Alım Katmanı', de: 'Datenaufnahme-Schicht' },
        components: 'WebSocket Streams · REST APIs · Market Feeds',
        color: 'default'
      },
      {
        id: 'processing',
        name: { en: 'Processing Layer', tr: 'İşleme Katmanı', de: 'Verarbeitungsschicht' },
        components: 'Async ETL · Event Queue · Stream Processing',
        color: 'default'
      },
      {
        id: 'model',
        name: { en: 'Model Layer', tr: 'Model Katmanı', de: 'Modellschicht' },
        components: 'Quant Strategy Engine · Risk Module · Signal Validator',
        color: 'default'
      },
      {
        id: 'api',
        name: { en: 'API Layer', tr: 'API Katmanı', de: 'API-Schicht' },
        components: 'Low-Latency Execution API · Auth Layer · Rate Limiter',
        color: 'default'
      },
      {
        id: 'infra',
        name: { en: 'Infrastructure', tr: 'Altyapı', de: 'Infrastruktur' },
        components: 'Auto-Scaling · Observability · Failover Strategy',
        color: 'heavy'
      }
    ],
    // THE PROBLEM
    problem: {
      context: {
        en: 'Financial markets produce high-frequency, noisy, and psychologically driven data streams that require millisecond-level processing for competitive advantage.',
        tr: 'Finansal piyasalar, rekabet avantajı için milisaniye seviyesinde işlem gerektiren yüksek frekanslı, gürültülü ve psikolojik olarak yönlendirilen veri akışları üretir.',
        de: 'Finanzmärkte erzeugen hochfrequente, verrauschte und psychologisch gesteuerte Datenströme, die eine Verarbeitung auf Millisekundenebene für Wettbewerbsvorteile erfordern.'
      },
      constraints: {
        en: 'Sub-50ms latency requirement, 99.9% uptime SLA, real-time data ingestion from multiple market feeds, and regulatory compliance for financial data handling.',
        tr: '50ms altı gecikme gereksinimi, %99.9 çalışma süresi SLA\'sı, birden fazla piyasa beslemesinden gerçek zamanlı veri alımı ve finansal veri işleme için düzenleyici uyumluluk.',
        de: 'Anforderung von unter 50 ms Latenz, 99,9 % Uptime-SLA, Echtzeit-Datenaufnahme aus mehreren Marktfeeds und regulatorische Compliance für die Finanzdatenverarbeitung.'
      },
      requirements: {
        en: 'Production-grade reliability for mission-critical trading decisions, horizontal scalability for market volatility spikes, and interpretable AI outputs for audit trails.',
        tr: 'Kritik ticaret kararları için production seviye güvenilirlik, piyasa volatilite artışları için yatay ölçeklenebilirlik ve denetim izleri için yorumlanabilir AI çıktıları.',
        de: 'Produktionsreife Zuverlässigkeit für geschäftskritische Handelsentscheidungen, horizontale Skalierbarkeit für Marktvolatilitätsspitzen und interpretierbare KI-Ausgaben für Audit-Trails.'
      }
    },
    // ENGINEERING APPROACH
    approach: {
      architecture: {
        en: 'Event-driven microservices architecture with dedicated processing pipelines for data ingestion, signal generation, and LLM-powered interpretation layers.',
        tr: 'Veri alımı, sinyal üretimi ve LLM destekli yorumlama katmanları için özel işlem hatları içeren olay odaklı mikroservis mimarisi.',
        de: 'Ereignisgesteuerte Microservices-Architektur mit dedizierten Verarbeitungspipelines für Datenaufnahme, Signalgenerierung und LLM-gestützte Interpretationsschichten.'
      },
      tradeoffs: {
        en: 'Prioritized latency over batch efficiency. Chose in-memory processing with Redis for hot data paths. Implemented circuit breakers for external API resilience.',
        tr: 'Gecikmeyi batch verimliliğinin önüne koydu. Sıcak veri yolları için Redis ile bellek içi işleme seçildi. Harici API dayanıklılığı için devre kesiciler uygulandı.',
        de: 'Latenz gegenüber Batch-Effizienz priorisiert. In-Memory-Verarbeitung mit Redis für Hot-Data-Pfade gewählt. Circuit Breaker für externe API-Resilienz implementiert.'
      },
      scalability: {
        en: 'Stateless compute nodes with Kubernetes auto-scaling. Message queue buffering for load spikes. Read replicas for analytics workloads without impacting core processing.',
        tr: 'Kubernetes otomatik ölçekleme ile durumsuz hesaplama düğümleri. Yük artışları için mesaj kuyruğu tamponlama. Çekirdek işlemeyi etkilemeden analitik iş yükleri için okuma replikaları.',
        de: 'Zustandslose Compute-Knoten mit Kubernetes-Auto-Scaling. Message-Queue-Pufferung für Lastspitzen. Read-Replicas für Analyseworkloads ohne Beeinträchtigung der Kernverarbeitung.'
      }
    },
    // SYSTEM ARCHITECTURE
    stack: ['Python', 'TensorFlow', 'FastAPI', 'Redis', 'PostgreSQL', 'Kubernetes'],
    infrastructure: {
      en: 'Cloud-native deployment on AWS with multi-AZ redundancy. WebSocket connections for real-time client updates. Prometheus + Grafana for observability.',
      tr: 'Çoklu AZ yedekliliği ile AWS üzerinde cloud-native deployment. Gerçek zamanlı istemci güncellemeleri için WebSocket bağlantıları. Gözlemlenebilirlik için Prometheus + Grafana.',
      de: 'Cloud-native Deployment auf AWS mit Multi-AZ-Redundanz. WebSocket-Verbindungen für Echtzeit-Client-Updates. Prometheus + Grafana für Observability.'
    },
    dataFlow: {
      en: 'Market feeds → Ingestion service → Signal processing → ML inference → LLM interpretation → WebSocket broadcast → Client dashboard',
      tr: 'Piyasa beslemeleri → Alım servisi → Sinyal işleme → ML çıkarımı → LLM yorumlama → WebSocket yayını → İstemci dashboard',
      de: 'Marktfeeds → Ingestion-Service → Signalverarbeitung → ML-Inferenz → LLM-Interpretation → WebSocket-Broadcast → Client-Dashboard'
    },
    // PERFORMANCE & RELIABILITY
    performance: {
      latency: { value: '<50ms', label: { en: 'End-to-end Response', tr: 'Uçtan Uca Yanıt', de: 'End-to-End-Antwort' } },
      throughput: { value: '1M+', label: { en: 'Daily Predictions', tr: 'Günlük Tahmin', de: 'Tägliche Vorhersagen' } },
      uptime: { value: '99.9%', label: { en: 'Production SLA', tr: 'Production SLA', de: 'Production-SLA' } },
      accuracy: { value: '99.2%', label: { en: 'Signal Accuracy', tr: 'Sinyal Doğruluğu', de: 'Signalgenauigkeit' } }
    },
    // IMPACT
    impact: {
      business: {
        en: 'Enabled data-driven trading decisions with institutional-grade signal reliability and real-time market context.',
        tr: 'Kurumsal seviye sinyal güvenilirliği ve gerçek zamanlı piyasa bağlamı ile veri odaklı ticaret kararlarını mümkün kıldı.',
        de: 'Ermöglichte datengesteuerte Handelsentscheidungen mit institutioneller Signalzuverlässigkeit und Echtzeit-Marktkontext.'
      },
      stability: {
        en: 'Zero critical incidents in 12 months of production operation. Automated failover tested and validated.',
        tr: '12 aylık production operasyonunda sıfır kritik olay. Otomatik yük devretme test edildi ve doğrulandı.',
        de: 'Null kritische Vorfälle in 12 Monaten Produktionsbetrieb. Automatisches Failover getestet und validiert.'
      },
      strategic: {
        en: 'Platform architecture supports expansion to new asset classes and market regions without core system changes.',
        tr: 'Platform mimarisi, çekirdek sistem değişikliği olmadan yeni varlık sınıflarına ve piyasa bölgelerine genişlemeyi destekler.',
        de: 'Plattformarchitektur unterstützt Expansion in neue Anlageklassen und Marktregionen ohne Kernsystemänderungen.'
      }
    },
    link: 'https://borsamex.com',
    year: '2024'
  },
  datextrader: {
    id: 'datextrader',
    name: 'DatexTrader',
    category: {
      en: 'Financial Infrastructure',
      tr: 'Finansal Altyapı',
      de: 'Finanzinfrastruktur'
    },
    systemType: {
      en: 'Enterprise Trading Infrastructure',
      tr: 'Kurumsal Ticaret Altyapısı',
      de: 'Enterprise-Handelsinfrastruktur'
    },
    tagline: {
      en: 'High-performance algorithmic trading platform',
      tr: 'Yüksek performanslı algoritmik ticaret platformu',
      de: 'Hochleistungs-Plattform für algorithmischen Handel'
    },
    // ARCHITECTURE LAYERS
    architectureLayers: [
      {
        id: 'ingestion',
        name: { en: 'Order Ingestion', tr: 'Emir Alım', de: 'Order-Eingang' },
        components: 'Strategy Signals · Risk Gateway · Order Validation',
        color: 'default'
      },
      {
        id: 'processing',
        name: { en: 'Execution Engine', tr: 'Yürütme Motoru', de: 'Ausführungs-Engine' },
        components: 'CQRS Pattern · Event Sourcing · Order Router',
        color: 'default'
      },
      {
        id: 'model',
        name: { en: 'Matching Layer', tr: 'Eşleştirme Katmanı', de: 'Matching-Schicht' },
        components: 'Order Book · Price Discovery · Trade Settlement',
        color: 'default'
      },
      {
        id: 'api',
        name: { en: 'Exchange Gateway', tr: 'Borsa Geçidi', de: 'Exchange-Gateway' },
        components: 'FIX Protocol · gRPC Services · Position Sync',
        color: 'default'
      },
      {
        id: 'infra',
        name: { en: 'Infrastructure', tr: 'Altyapı', de: 'Infrastruktur' },
        components: 'Bare-Metal · Multi-Region DR · Audit Logging',
        color: 'heavy'
      }
    ],
    problem: {
      context: {
        en: 'Algorithmic trading demands precise execution, microsecond-level latency, and fault-tolerant infrastructure that can handle extreme market conditions.',
        tr: 'Algoritmik ticaret, hassas yürütme, mikrosaniye seviyesinde gecikme ve aşırı piyasa koşullarını kaldırabilecek hata toleranslı altyapı gerektirir.',
        de: 'Algorithmischer Handel erfordert präzise Ausführung, Latenz auf Mikrosekundenebene und fehlertolerante Infrastruktur für extreme Marktbedingungen.'
      },
      constraints: {
        en: '99.99% uptime requirement, 10K+ orders per second throughput, sub-millisecond order routing, and real-time risk management integration.',
        tr: '%99.99 çalışma süresi gereksinimi, saniyede 10K+ emir verimi, milisaniye altı emir yönlendirme ve gerçek zamanlı risk yönetimi entegrasyonu.',
        de: '99,99% Uptime-Anforderung, 10K+ Orders pro Sekunde Durchsatz, Sub-Millisekunden Order-Routing und Echtzeit-Risikomanagement-Integration.'
      },
      requirements: {
        en: 'Modular architecture for strategy deployment, comprehensive audit logging, and regulatory-compliant transaction processing.',
        tr: 'Strateji dağıtımı için modüler mimari, kapsamlı denetim günlüğü ve düzenleyici uyumlu işlem işleme.',
        de: 'Modulare Architektur für Strategiebereitstellung, umfassende Audit-Protokollierung und regulatorisch konforme Transaktionsverarbeitung.'
      }
    },
    approach: {
      architecture: {
        en: 'Low-latency event sourcing with CQRS pattern. Separate read/write paths for order execution and analytics. gRPC for internal service communication.',
        tr: 'CQRS deseni ile düşük gecikmeli olay kaynağı. Emir yürütme ve analitik için ayrı okuma/yazma yolları. Dahili servis iletişimi için gRPC.',
        de: 'Low-Latency Event Sourcing mit CQRS-Pattern. Separate Lese-/Schreibpfade für Orderausführung und Analytics. gRPC für interne Service-Kommunikation.'
      },
      tradeoffs: {
        en: 'Chose Go for performance-critical order matching engine. Accepted higher development complexity for deterministic latency guarantees.',
        tr: 'Performans kritik emir eşleştirme motoru için Go seçildi. Deterministik gecikme garantileri için daha yüksek geliştirme karmaşıklığı kabul edildi.',
        de: 'Go für performance-kritische Order-Matching-Engine gewählt. Höhere Entwicklungskomplexität für deterministische Latenzgarantien akzeptiert.'
      },
      scalability: {
        en: 'Horizontal scaling via consistent hashing for order distribution. In-memory order book with persistent snapshots. Kubernetes for orchestration.',
        tr: 'Emir dağıtımı için tutarlı hashing ile yatay ölçekleme. Kalıcı anlık görüntüler ile bellek içi emir defteri. Orkestrasyon için Kubernetes.',
        de: 'Horizontale Skalierung via Consistent Hashing für Order-Verteilung. In-Memory Order Book mit persistenten Snapshots. Kubernetes für Orchestrierung.'
      }
    },
    stack: ['Go', 'PostgreSQL', 'Redis', 'Kubernetes', 'gRPC', 'Prometheus'],
    infrastructure: {
      en: 'Bare-metal deployment in co-located data centers for minimal latency. Multi-region disaster recovery with active-passive failover.',
      tr: 'Minimum gecikme için ortak konumlandırılmış veri merkezlerinde bare-metal deployment. Aktif-pasif yük devretme ile çoklu bölge felaket kurtarma.',
      de: 'Bare-Metal-Deployment in Co-Location-Rechenzentren für minimale Latenz. Multi-Region Disaster Recovery mit Active-Passive Failover.'
    },
    dataFlow: {
      en: 'Strategy signals → Risk check → Order router → Matching engine → Exchange gateway → Execution confirmation → Position update',
      tr: 'Strateji sinyalleri → Risk kontrolü → Emir yönlendirici → Eşleştirme motoru → Borsa geçidi → Yürütme onayı → Pozisyon güncelleme',
      de: 'Strategie-Signale → Risiko-Check → Order-Router → Matching-Engine → Exchange-Gateway → Ausführungsbestätigung → Positions-Update'
    },
    performance: {
      latency: { value: '<1ms', label: { en: 'Order Routing', tr: 'Emir Yönlendirme', de: 'Order-Routing' } },
      throughput: { value: '10K+', label: { en: 'Orders/Second', tr: 'Emir/Saniye', de: 'Orders/Sekunde' } },
      uptime: { value: '99.99%', label: { en: 'Production SLA', tr: 'Production SLA', de: 'Production-SLA' } },
      accuracy: { value: '€50M+', label: { en: 'Volume Processed', tr: 'İşlem Hacmi', de: 'Verarbeitetes Volumen' } }
    },
    impact: {
      business: {
        en: 'Enabled institutional-grade algorithmic trading capabilities with enterprise risk controls and regulatory compliance.',
        tr: 'Kurumsal risk kontrolleri ve düzenleyici uyumluluk ile kurumsal seviye algoritmik ticaret yeteneklerini mümkün kıldı.',
        de: 'Ermöglichte algorithmische Handelsfähigkeiten auf institutionellem Niveau mit Enterprise-Risikokontrollen und regulatorischer Compliance.'
      },
      stability: {
        en: 'Maintained 99.99% uptime through two major market volatility events. Zero data loss incidents.',
        tr: 'İki büyük piyasa volatilite olayı boyunca %99.99 çalışma süresi korundu. Sıfır veri kaybı olayı.',
        de: 'Aufrechterhaltung von 99,99% Uptime während zwei großer Marktvolatilitätsereignisse. Null Datenverlust-Vorfälle.'
      },
      strategic: {
        en: 'White-label ready architecture enabling rapid deployment for new institutional clients.',
        tr: 'Yeni kurumsal müşteriler için hızlı dağıtımı mümkün kılan white-label hazır mimari.',
        de: 'White-Label-fähige Architektur für schnelle Bereitstellung bei neuen institutionellen Kunden.'
      }
    },
    link: 'https://datextrader.com',
    year: '2024'
  },
  izmexcarbon: {
    id: 'izmexcarbon',
    name: 'İzmexCarbon',
    category: {
      en: 'Enterprise Platform',
      tr: 'Kurumsal Platform',
      de: 'Enterprise-Plattform'
    },
    systemType: {
      en: 'Carbon Intelligence & ESG Analytics Platform',
      tr: 'Karbon İstihbaratı & ESG Analitik Platformu',
      de: 'Carbon-Intelligence & ESG-Analytics-Plattform'
    },
    tagline: {
      en: 'Automated sustainability tracking and reporting',
      tr: 'Otomatik sürdürülebilirlik takibi ve raporlama',
      de: 'Automatisierte Nachhaltigkeitsverfolgung und -berichterstattung'
    },
    // ARCHITECTURE LAYERS
    architectureLayers: [
      {
        id: 'ingestion',
        name: { en: 'Data Collection', tr: 'Veri Toplama', de: 'Datenerfassung' },
        components: 'Multi-Format Import · API Connectors · Manual Entry',
        color: 'default'
      },
      {
        id: 'processing',
        name: { en: 'ETL Pipeline', tr: 'ETL Pipeline', de: 'ETL-Pipeline' },
        components: 'Data Normalization · Validation Rules · Tenant Isolation',
        color: 'default'
      },
      {
        id: 'model',
        name: { en: 'Calculation Engine', tr: 'Hesaplama Motoru', de: 'Berechnungs-Engine' },
        components: 'Carbon Footprint · GRI Standards · Compliance Rules',
        color: 'default'
      },
      {
        id: 'api',
        name: { en: 'Visualization Layer', tr: 'Görselleştirme', de: 'Visualisierung' },
        components: 'D3.js Charts · Report Builder · Export Engine',
        color: 'default'
      },
      {
        id: 'infra',
        name: { en: 'Infrastructure', tr: 'Altyapı', de: 'Infrastruktur' },
        components: 'Serverless · CDN Delivery · Database Sharding',
        color: 'heavy'
      }
    ],
    problem: {
      context: {
        en: 'Enterprises face increasing regulatory pressure for ESG compliance, with fragmented data sources and manual reporting processes creating operational bottlenecks.',
        tr: 'Kurumlar, parçalanmış veri kaynakları ve manuel raporlama süreçleri operasyonel darboğazlar oluştururken, ESG uyumluluğu için artan düzenleyici baskıyla karşı karşıya.',
        de: 'Unternehmen stehen unter zunehmendem regulatorischem Druck für ESG-Compliance, wobei fragmentierte Datenquellen und manuelle Berichtsprozesse operative Engpässe verursachen.'
      },
      constraints: {
        en: 'Multiple data format ingestion, regulatory reporting standards (GRI, CDP, TCFD), multi-tenant architecture for B2B SaaS model.',
        tr: 'Çoklu veri format alımı, düzenleyici raporlama standartları (GRI, CDP, TCFD), B2B SaaS modeli için çok kiracılı mimari.',
        de: 'Aufnahme mehrerer Datenformate, regulatorische Berichtsstandards (GRI, CDP, TCFD), Multi-Tenant-Architektur für B2B-SaaS-Modell.'
      },
      requirements: {
        en: 'Automated data collection pipelines, calculation engines for carbon footprint metrics, and audit-ready report generation.',
        tr: 'Otomatik veri toplama hatları, karbon ayak izi metrikleri için hesaplama motorları ve denetim hazır rapor oluşturma.',
        de: 'Automatisierte Datenerfassungspipelines, Berechnungsengines für CO2-Fußabdruck-Metriken und audit-bereite Berichtserstellung.'
      }
    },
    approach: {
      architecture: {
        en: 'Multi-tenant SaaS architecture with tenant isolation at database level. ETL pipelines for data normalization. Rule engine for compliance calculations.',
        tr: 'Veritabanı seviyesinde kiracı izolasyonu ile çok kiracılı SaaS mimarisi. Veri normalizasyonu için ETL hatları. Uyumluluk hesaplamaları için kural motoru.',
        de: 'Multi-Tenant-SaaS-Architektur mit Tenant-Isolation auf Datenbankebene. ETL-Pipelines für Datennormalisierung. Rule-Engine für Compliance-Berechnungen.'
      },
      tradeoffs: {
        en: 'Chose flexibility over rigid schema to accommodate varying ESG frameworks. Implemented calculation versioning for regulatory changes.',
        tr: 'Değişen ESG çerçevelerini barındırmak için katı şema yerine esneklik seçildi. Düzenleyici değişiklikler için hesaplama sürümleme uygulandı.',
        de: 'Flexibilität gegenüber starrem Schema für unterschiedliche ESG-Frameworks gewählt. Berechnungsversionierung für regulatorische Änderungen implementiert.'
      },
      scalability: {
        en: 'Async job processing for heavy report generation. CDN for report delivery. Database sharding strategy for enterprise growth.',
        tr: 'Ağır rapor oluşturma için asenkron iş işleme. Rapor dağıtımı için CDN. Kurumsal büyüme için veritabanı parçalama stratejisi.',
        de: 'Asynchrone Job-Verarbeitung für aufwendige Berichtserstellung. CDN für Report-Delivery. Database-Sharding-Strategie für Enterprise-Wachstum.'
      }
    },
    stack: ['React', 'Node.js', 'MongoDB', 'Redis', 'AWS Lambda', 'D3.js'],
    infrastructure: {
      en: 'Serverless compute for variable workloads. S3 for document storage. CloudFront for global report delivery.',
      tr: 'Değişken iş yükleri için sunucusuz hesaplama. Belge depolama için S3. Global rapor dağıtımı için CloudFront.',
      de: 'Serverless-Compute für variable Workloads. S3 für Dokumentenspeicherung. CloudFront für globale Report-Delivery.'
    },
    dataFlow: {
      en: 'Data sources → ETL pipeline → Normalization → Calculation engine → Visualization layer → Report generator → Compliance export',
      tr: 'Veri kaynakları → ETL hattı → Normalizasyon → Hesaplama motoru → Görselleştirme katmanı → Rapor oluşturucu → Uyumluluk dışa aktarma',
      de: 'Datenquellen → ETL-Pipeline → Normalisierung → Berechnungsengine → Visualisierungsschicht → Report-Generator → Compliance-Export'
    },
    performance: {
      latency: { value: '<2s', label: { en: 'Dashboard Load', tr: 'Dashboard Yükleme', de: 'Dashboard-Laden' } },
      throughput: { value: '50+', label: { en: 'Companies Onboard', tr: 'Şirket', de: 'Unternehmen' } },
      uptime: { value: '99.9%', label: { en: 'Platform SLA', tr: 'Platform SLA', de: 'Plattform-SLA' } },
      accuracy: { value: '-32%', label: { en: 'Avg Carbon Reduction', tr: 'Ort. Karbon Azaltımı', de: 'Durchschn. CO₂-Reduktion' } }
    },
    impact: {
      business: {
        en: 'Reduced ESG reporting time by 85% while improving data accuracy and regulatory compliance readiness.',
        tr: 'Veri doğruluğunu ve düzenleyici uyumluluk hazırlığını iyileştirirken ESG raporlama süresini %85 azalttı.',
        de: 'Reduzierung der ESG-Berichtszeit um 85% bei gleichzeitiger Verbesserung der Datengenauigkeit und regulatorischen Compliance-Bereitschaft.'
      },
      stability: {
        en: 'Successfully processed reporting cycles for 50+ enterprises without data integrity issues.',
        tr: 'Veri bütünlüğü sorunları olmadan 50\'den fazla kuruluş için raporlama döngülerini başarıyla işledi.',
        de: 'Erfolgreiche Verarbeitung von Berichtszyklen für 50+ Unternehmen ohne Datenintegritätsprobleme.'
      },
      strategic: {
        en: 'Platform positioned for EU CSRD compliance requirements, expanding addressable market significantly.',
        tr: 'Platform, adreslenebilir pazarı önemli ölçüde genişleterek AB CSRD uyumluluk gereksinimleri için konumlandırıldı.',
        de: 'Plattform positioniert für EU-CSRD-Compliance-Anforderungen, signifikante Erweiterung des adressierbaren Marktes.'
      }
    },
    link: 'https://izmexcarbon.com',
    year: '2023'
  },
  izmexmedia: {
    id: 'izmexmedia',
    name: 'İzmexMedia',
    category: {
      en: 'Enterprise Platform',
      tr: 'Kurumsal Platform',
      de: 'Enterprise-Plattform'
    },
    systemType: {
      en: 'Reputation & Workflow Management System',
      tr: 'İtibar & İş Akışı Yönetim Sistemi',
      de: 'Reputations- & Workflow-Management-System'
    },
    tagline: {
      en: 'Operational intelligence for digital agencies',
      tr: 'Dijital ajanslar için operasyonel istihbarat',
      de: 'Operative Intelligenz für digitale Agenturen'
    },
    // ARCHITECTURE LAYERS
    architectureLayers: [
      {
        id: 'ingestion',
        name: { en: 'Platform Integration', tr: 'Platform Entegrasyonu', de: 'Plattform-Integration' },
        components: '20+ API Connectors · Webhook Listeners · Data Sync',
        color: 'default'
      },
      {
        id: 'processing',
        name: { en: 'Event Processing', tr: 'Olay İşleme', de: 'Event-Verarbeitung' },
        components: 'Notification Engine · Workflow Triggers · Alert Rules',
        color: 'default'
      },
      {
        id: 'model',
        name: { en: 'Analytics Engine', tr: 'Analitik Motoru', de: 'Analytics-Engine' },
        components: 'Cross-Client Metrics · Reputation Scoring · Trends',
        color: 'default'
      },
      {
        id: 'api',
        name: { en: 'Presentation Layer', tr: 'Sunum Katmanı', de: 'Präsentationsschicht' },
        components: 'Multi-Tenant Dashboard · White-Label · Report Export',
        color: 'default'
      },
      {
        id: 'infra',
        name: { en: 'Infrastructure', tr: 'Altyapı', de: 'Infrastruktur' },
        components: 'Docker · Read Replicas · Job Queue · Caching',
        color: 'heavy'
      }
    ],
    problem: {
      context: {
        en: 'Digital agencies manage complex multi-client workflows with fragmented tools, leading to operational inefficiencies and limited visibility into performance metrics.',
        tr: 'Dijital ajanslar, parçalanmış araçlarla karmaşık çok müşterili iş akışlarını yönetir, bu da operasyonel verimsizliklere ve performans metriklerinde sınırlı görünürlüğe yol açar.',
        de: 'Digitale Agenturen verwalten komplexe Multi-Client-Workflows mit fragmentierten Tools, was zu operativen Ineffizienzen und eingeschränkter Sichtbarkeit in Performance-Metriken führt.'
      },
      constraints: {
        en: '24/7 monitoring requirements, multi-tenant client isolation, real-time alerting systems, and integration with 20+ external platforms.',
        tr: '7/24 izleme gereksinimleri, çok kiracılı müşteri izolasyonu, gerçek zamanlı uyarı sistemleri ve 20\'den fazla harici platformla entegrasyon.',
        de: '24/7-Überwachungsanforderungen, Multi-Tenant-Client-Isolation, Echtzeit-Alerting-Systeme und Integration mit 20+ externen Plattformen.'
      },
      requirements: {
        en: 'Unified dashboard for cross-client analytics, automated workflow triggers, and white-label capabilities for agency branding.',
        tr: 'Çapraz müşteri analitiği için birleşik dashboard, otomatik iş akışı tetikleyicileri ve ajans markalaması için white-label yetenekleri.',
        de: 'Einheitliches Dashboard für Cross-Client-Analytics, automatisierte Workflow-Trigger und White-Label-Fähigkeiten für Agentur-Branding.'
      }
    },
    approach: {
      architecture: {
        en: 'Modular monolith with clear domain boundaries. Event-driven notifications. Plugin architecture for platform integrations.',
        tr: 'Net alan sınırları ile modüler monolit. Olay odaklı bildirimler. Platform entegrasyonları için plugin mimarisi.',
        de: 'Modularer Monolith mit klaren Domain-Grenzen. Event-gesteuerte Benachrichtigungen. Plugin-Architektur für Plattform-Integrationen.'
      },
      tradeoffs: {
        en: 'Prioritized time-to-market with monolith, designed for future microservices extraction. Chose PostgreSQL for ACID guarantees on financial data.',
        tr: 'Monolit ile pazara çıkış süresine öncelik verildi, gelecekteki mikroservis çıkarımı için tasarlandı. Finansal veriler üzerinde ACID garantileri için PostgreSQL seçildi.',
        de: 'Time-to-Market mit Monolith priorisiert, Design für zukünftige Microservices-Extraktion. PostgreSQL für ACID-Garantien auf Finanzdaten gewählt.'
      },
      scalability: {
        en: 'Read replicas for reporting workloads. Background job processing for heavy analytics. Caching layer for frequently accessed dashboards.',
        tr: 'Raporlama iş yükleri için okuma replikaları. Ağır analitikler için arka plan iş işleme. Sık erişilen dashboardlar için önbellek katmanı.',
        de: 'Read-Replicas für Reporting-Workloads. Background-Job-Processing für schwere Analytics. Caching-Layer für häufig aufgerufene Dashboards.'
      }
    },
    stack: ['Vue.js', 'Python', 'PostgreSQL', 'Redis', 'Docker', 'Celery'],
    infrastructure: {
      en: 'Docker Compose for development parity. PostgreSQL with streaming replication. Redis for caching and job queuing.',
      tr: 'Geliştirme eşliği için Docker Compose. Akış replikasyonu ile PostgreSQL. Önbellek ve iş kuyruğu için Redis.',
      de: 'Docker Compose für Development-Parity. PostgreSQL mit Streaming-Replikation. Redis für Caching und Job-Queuing.'
    },
    dataFlow: {
      en: 'External APIs → Integration layer → Data normalization → Analytics engine → Dashboard → Alert system → Client notifications',
      tr: 'Harici API\'ler → Entegrasyon katmanı → Veri normalizasyonu → Analitik motoru → Dashboard → Uyarı sistemi → Müşteri bildirimleri',
      de: 'Externe APIs → Integrationsschicht → Datennormalisierung → Analytics-Engine → Dashboard → Alert-System → Client-Benachrichtigungen'
    },
    performance: {
      latency: { value: '<1s', label: { en: 'Dashboard Refresh', tr: 'Dashboard Yenileme', de: 'Dashboard-Refresh' } },
      throughput: { value: '200+', label: { en: 'Active Clients', tr: 'Aktif Müşteri', de: 'Aktive Kunden' } },
      uptime: { value: '24/7', label: { en: 'Monitoring', tr: 'İzleme', de: 'Überwachung' } },
      accuracy: { value: '3x', label: { en: 'Productivity Gain', tr: 'Verimlilik Artışı', de: 'Produktivitätssteigerung' } }
    },
    impact: {
      business: {
        en: 'Tripled agency operational efficiency while providing comprehensive client visibility and automated reporting.',
        tr: 'Kapsamlı müşteri görünürlüğü ve otomatik raporlama sağlarken ajans operasyonel verimliliğini üç katına çıkardı.',
        de: 'Verdreifachung der Agentur-Betriebseffizienz bei gleichzeitiger umfassender Client-Sichtbarkeit und automatisiertem Reporting.'
      },
      stability: {
        en: 'Platform maintained 24/7 monitoring uptime with automated failover and alerting.',
        tr: 'Platform, otomatik yük devretme ve uyarı ile 7/24 izleme çalışma süresini korudu.',
        de: 'Plattform hielt 24/7-Monitoring-Uptime mit automatisiertem Failover und Alerting aufrecht.'
      },
      strategic: {
        en: 'Established as the operational backbone for agency scaling, supporting 200+ concurrent client accounts.',
        tr: 'Ajans ölçekleme için operasyonel omurga olarak kuruldu, 200\'den fazla eşzamanlı müşteri hesabını destekliyor.',
        de: 'Etabliert als operatives Rückgrat für Agentur-Skalierung, Unterstützung von 200+ gleichzeitigen Client-Accounts.'
      }
    },
    link: 'https://izmexmedia.com',
    year: '2023'
  },
  izmex: {
    id: 'izmex',
    name: 'İzmex',
    category: {
      en: 'B2B Platform',
      tr: 'B2B Platform',
      de: 'B2B-Plattform'
    },
    systemType: {
      en: 'Industrial B2B Sourcing Platform',
      tr: 'Endüstriyel B2B Tedarik Platformu',
      de: 'Industrielle B2B-Beschaffungsplattform'
    },
    tagline: {
      en: 'Multi-vendor marketplace connecting manufacturers to global markets',
      tr: 'Üreticileri global pazarlara bağlayan çok satıcılı pazar yeri',
      de: 'Multi-Vendor-Marktplatz für Hersteller und globale Märkte'
    },
    // ARCHITECTURE LAYERS
    architectureLayers: [
      {
        id: 'ingestion',
        name: { en: 'Catalog Ingestion', tr: 'Katalog Alımı', de: 'Katalog-Aufnahme' },
        components: 'Bulk Import · API Sync · Manual Entry · Image Processing',
        color: 'default'
      },
      {
        id: 'processing',
        name: { en: 'Search & Discovery', tr: 'Arama & Keşif', de: 'Suche & Entdeckung' },
        components: 'Elasticsearch · Faceted Search · Recommendations',
        color: 'default'
      },
      {
        id: 'model',
        name: { en: 'Matching Engine', tr: 'Eşleştirme Motoru', de: 'Matching-Engine' },
        components: 'Buyer-Seller Match · RFQ System · Lead Scoring',
        color: 'default'
      },
      {
        id: 'api',
        name: { en: 'Multi-Tenant Layer', tr: 'Çok Kiracılı Katman', de: 'Multi-Tenant-Schicht' },
        components: 'Vendor Portals · Buyer Dashboard · Admin Panel',
        color: 'default'
      },
      {
        id: 'infra',
        name: { en: 'Infrastructure', tr: 'Altyapı', de: 'Infrastruktur' },
        components: 'CDN · Auto-Scaling · Multi-Region · Caching',
        color: 'heavy'
      }
    ],
    problem: {
      context: {
        en: 'Turkish manufacturers struggle to reach international buyers due to fragmented discovery channels and language barriers in B2B trade.',
        tr: 'Türk üreticiler, B2B ticaretinde parçalanmış keşif kanalları ve dil bariyerleri nedeniyle uluslararası alıcılara ulaşmakta zorlanıyor.',
        de: 'Türkische Hersteller haben Schwierigkeiten, internationale Käufer zu erreichen, aufgrund fragmentierter Entdeckungskanäle und Sprachbarrieren im B2B-Handel.'
      },
      constraints: {
        en: 'Multi-language catalog support, high-volume product data, complex categorization taxonomy, and real-time inventory synchronization.',
        tr: 'Çok dilli katalog desteği, yüksek hacimli ürün verisi, karmaşık kategorizasyon taksonomisi ve gerçek zamanlı envanter senkronizasyonu.',
        de: 'Mehrsprachige Katalogunterstützung, hochvolumige Produktdaten, komplexe Kategorisierungstaxonomie und Echtzeit-Bestandssynchronisation.'
      },
      requirements: {
        en: 'Scalable product catalog architecture, intelligent search and filtering, RFQ workflow automation, and export-focused discovery tools.',
        tr: 'Ölçeklenebilir ürün kataloğu mimarisi, akıllı arama ve filtreleme, RFQ iş akışı otomasyonu ve ihracat odaklı keşif araçları.',
        de: 'Skalierbare Produktkatalog-Architektur, intelligente Suche und Filterung, RFQ-Workflow-Automatisierung und exportorientierte Entdeckungstools.'
      }
    },
    approach: {
      architecture: {
        en: 'Microservices architecture with dedicated services for catalog, search, messaging, and analytics. Event-driven communication between services.',
        tr: 'Katalog, arama, mesajlaşma ve analitik için özel servislerle mikroservis mimarisi. Servisler arası olay odaklı iletişim.',
        de: 'Microservices-Architektur mit dedizierten Services für Katalog, Suche, Messaging und Analytics. Event-gesteuerte Kommunikation zwischen Services.'
      },
      tradeoffs: {
        en: 'Chose Elasticsearch for search performance over SQL full-text. Implemented eventual consistency for catalog updates to handle bulk imports.',
        tr: 'SQL tam metin yerine arama performansı için Elasticsearch seçildi. Toplu alımları işlemek için katalog güncellemelerinde eventual consistency uygulandı.',
        de: 'Elasticsearch für Suchperformance gegenüber SQL-Volltext gewählt. Eventual Consistency für Katalog-Updates zur Handhabung von Bulk-Imports implementiert.'
      },
      scalability: {
        en: 'CDN for static assets and product images. Read replicas for search queries. Horizontal scaling for catalog service during peak import periods.',
        tr: 'Statik varlıklar ve ürün görselleri için CDN. Arama sorguları için okuma replikaları. Yoğun alım dönemlerinde katalog servisi için yatay ölçekleme.',
        de: 'CDN für statische Assets und Produktbilder. Read-Replicas für Suchanfragen. Horizontale Skalierung für Katalog-Service während Peak-Import-Zeiten.'
      }
    },
    stack: ['React', 'Node.js', 'Elasticsearch', 'PostgreSQL', 'Redis', 'AWS'],
    infrastructure: {
      en: 'AWS multi-region deployment with CloudFront CDN. Elasticsearch cluster for search. S3 for product media storage with image optimization pipeline.',
      tr: 'CloudFront CDN ile AWS çoklu bölge deployment. Arama için Elasticsearch kümesi. Görüntü optimizasyon hattı ile ürün medya depolama için S3.',
      de: 'AWS Multi-Region-Deployment mit CloudFront CDN. Elasticsearch-Cluster für Suche. S3 für Produktmedienspeicherung mit Bildoptimierungspipeline.'
    },
    dataFlow: {
      en: 'Vendor upload → Catalog ingestion → Image optimization → Search indexing → Buyer discovery → RFQ creation → Lead notification → Transaction',
      tr: 'Satıcı yükleme → Katalog alımı → Görüntü optimizasyonu → Arama indeksleme → Alıcı keşfi → RFQ oluşturma → Lead bildirimi → İşlem',
      de: 'Vendor-Upload → Katalog-Aufnahme → Bildoptimierung → Such-Indexierung → Käufer-Entdeckung → RFQ-Erstellung → Lead-Benachrichtigung → Transaktion'
    },
    performance: {
      latency: { value: '<200ms', label: { en: 'Search Response', tr: 'Arama Yanıtı', de: 'Such-Antwort' } },
      throughput: { value: '12K+', label: { en: 'Manufacturers', tr: 'Üretici', de: 'Hersteller' } },
      uptime: { value: '150+', label: { en: 'Export Markets', tr: 'İhracat Pazarı', de: 'Exportmärkte' } },
      accuracy: { value: '500K+', label: { en: 'Products Listed', tr: 'Listelenen Ürün', de: 'Gelistete Produkte' } }
    },
    impact: {
      business: {
        en: 'Connected 12,000+ Turkish manufacturers to 150+ international export markets, facilitating B2B discovery and trade.',
        tr: '12.000\'den fazla Türk üreticiyi 150\'den fazla uluslararası ihracat pazarına bağlayarak B2B keşfi ve ticaretini kolaylaştırdı.',
        de: 'Verbindung von 12.000+ türkischen Herstellern mit 150+ internationalen Exportmärkten, Erleichterung von B2B-Entdeckung und Handel.'
      },
      stability: {
        en: 'Platform handles 500K+ product listings with sub-200ms search performance across all markets.',
        tr: 'Platform, tüm pazarlarda 200ms altı arama performansıyla 500K\'dan fazla ürün listelemesini yönetiyor.',
        de: 'Plattform verarbeitet 500K+ Produktlistings mit unter 200ms Suchperformance in allen Märkten.'
      },
      strategic: {
        en: 'Positioned as the primary digital export channel for Turkish industrial manufacturers seeking international buyers.',
        tr: 'Uluslararası alıcı arayan Türk endüstriyel üreticileri için birincil dijital ihracat kanalı olarak konumlandırıldı.',
        de: 'Positioniert als primärer digitaler Exportkanal für türkische Industriehersteller auf der Suche nach internationalen Käufern.'
      }
    },
    link: 'https://izmex.com',
    year: '2024'
  }
};

export const projectOrder = ['borsamex', 'datextrader', 'izmex', 'izmexcarbon'];
